"use client";

import { useEffect, useState } from "react";
import { isFilled } from "@prismicio/client";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css/core';
import "./carrousel-slider.css";
import { PrismicImage, PrismicLink, PrismicRichText } from "@prismicio/react";

const CardDefault = ({ recipe, isCarousel }) => {
  if (!recipe?.data) {
    return null;
  }

  return (
    <div className={`p-4 bg-light-${recipe.data.bg_color} overflow-hidden rounded-2xl flex flex-col gap-3 h-full w-full ${isCarousel ? 'max-w-[500px]' : 'max-w-[370px]'}`}>
      <div className="w-full overflow-hidden rounded-2xl ">
        <PrismicImage field={recipe.data.img} className={`transition duration-300 h-65 md:h-[340px] w-full object-cover ${isCarousel ? "group-[&.is-visible:hover]:scale-115" : "group-hover:scale-115"}`} />
      </div>
      <PrismicRichText field={recipe.data.text} components={{
        heading3: ({ children }) => <h3 className={`h4 leading-5 md:text-2xl font-bold line-clamp-3 md:leading-6 text-dark-${recipe.data.bg_color}`}>{children}</h3>,
        heading4: ({ children }) => <h4 className={`h4 leading-5 md:text-2xl font-bold line-clamp-3 md:leading-6 text-dark-${recipe.data.bg_color}`}>{children}</h4>,
      }} />
    </div>
  );
};


/**
 * @typedef {import("@prismicio/client").Content.CarrouselSlice} CarrouselSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<CarrouselSlice>} CarrouselProps
 * @type {import("react").FC<CarrouselProps>}
 */
const CarouselRecipes = ({ slice }) => {
  const recipes = (slice.primary.recipes || []).filter(({ recipe }) => recipe?.data);
  const [deviceType, setDeviceType] = useState("desktop");
  const [isLargeDesktop, setIsLargeDesktop] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 768) {
        setDeviceType("mobile");
      } else if (width >= 768 && width < 1110) {
        setDeviceType("tablet");
      } else {
        setDeviceType("desktop");
      }

      setIsLargeDesktop(width >= 1200);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showDesktopSlider = deviceType === "desktop" && (
    (isLargeDesktop && recipes.length > 4) || 
    (!isLargeDesktop && recipes.length > 3)
  );
  const showTabletSlider = deviceType === "tablet" && recipes.length > 2;
  const showDesktopGrid = deviceType === "desktop" && (
    (isLargeDesktop && recipes.length <= 4) || 
    (!isLargeDesktop && recipes.length <= 3)
  );
  const showTabletGrid = deviceType === "tablet" && recipes.length <= 2;
  const showMobileSlider = deviceType === "mobile" && recipes.length >= 1;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-dark-${slice.primary.bg_color} ${slice.primary.bg_color == "beige" ? 'bg-beige' : ''} flex flex-col overflow-visible py-10 md:py-20 ${showDesktopSlider || showTabletSlider ? '' : 'md:px-20 gap-6'}`}
    >
      {isFilled.keyText(slice.primary.title) && (
        <div className={`flex flex-row ${showDesktopSlider || showTabletSlider ? 'items-start justify-start px-20' : 'items-center justify-center'}`}>
          <PrismicRichText field={slice.primary.title} components={{
            heading2: ({ children }) => <h2 className={`h2 ${slice.primary.bg_color == "beige" ? 'text-moyam-orange' : `text-light-${slice.primary.bg_color}`}`}>{children}</h2>,
            heading3: ({ children }) => <h3 className={`h3 ${slice.primary.bg_color == "beige" ? 'text-moyam-orange' : `text-light-${slice.primary.bg_color}`}`}>{children}</h3>,
          }} />
        </div>

      )}

      {recipes.length > 0 && (
        <>
          {showMobileSlider && (
            <div>
              <div className="px-5 gap-2 flex snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-scroll">
                {recipes.map(({ recipe }, index) => (
                  <div
                    key={index}
                    className="snap-center snap-always flex-shrink-0 w-[75vw] max-w-[360px]"
                  >
                    <CardDefault
                      recipe={recipe}
                      isCarousel={false}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {showDesktopSlider && (
            <div className={`overflow-visible carrousel-recipes  carrousel-recipes-${slice.primary.bg_color} py-6`}>
              <Splide
                options={{
                  perPage: 4,
                  perMove: 1,
                  gap: "1rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "10em", left: "5rem" },
                  breakpoints: {
                    1199: {
                      perPage: 3,
                    },
                  },
                }}
                aria-label="Gammes"
              >
                {recipes.map(({ recipe }, index) => (
                  <SplideSlide key={index} className="group overflow-visible">
                    <CardDefault
                      recipe={recipe}
                      isCarousel={true}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showTabletSlider && (
            <div className={`overflow-visible carrousel-recipes carrousel-recipes-${slice.primary.bg_color}  py-6`}>
              <Splide
                options={{
                  perPage: 2,
                  perMove: 1,
                  gap: "1rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "5rem", left: "5rem" },
                }}
                aria-label="Gammes"
              >
                {recipes.map(({ recipe }, index) => (
                  <SplideSlide key={index} className="group overflow-visible">
                    <CardDefault
                      recipe={recipe}
                      isCarousel={true}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showDesktopGrid && (
            <div className={`flex flex-row justify-center ${recipes.length === 4 ? 'gap-3' : 'gap-5'}`}>
              {recipes.map(({ recipe }, index) => (
                <div key={index} className={`group ${recipes.length === 4 ? 'w-1/4' : ''}`}>
                  <CardDefault
                    recipe={recipe}
                    isCarousel={false}
                  />
                </div>
              ))}
            </div>
          )}

          {showTabletGrid && (
            <div className="flex flex-row justify-centerp gap-5">
              {recipes.map(({ recipe }, index) => (
                <div key={index} className="group">
                  <CardDefault
                    recipe={recipe}
                    isCarousel={false}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isFilled.link(slice.primary.cta) && (
        <div className={`flex flex-row justify-center items-center ${slice.primary.bg_color == "beige" ? 'text-beige' : `text-dark-${slice.primary.bg_color}`}`}>
          <PrismicLink field={slice.primary.cta} className={`cta ${slice.primary.bg_color == "beige" ? 'cta-moyam-orange' : `cta-light-${slice.primary.bg_color}`}   self-end flex items-center gap-3`}>
            <svg width="24" height="24" viewBox="0 0 24 24" className="fill-current" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 15.0638V23.234C24 23.4372 23.9193 23.632 23.7757 23.7757C23.632 23.9193 23.4372 24 23.234 24H0.765957C0.562813 24 0.367989 23.9193 0.224344 23.7757C0.0806988 23.632 0 23.4372 0 23.234V15.0638C0 14.8607 0.0806988 14.6659 0.224344 14.5222C0.367989 14.3786 0.562813 14.2979 0.765957 14.2979C0.969102 14.2979 1.16393 14.3786 1.30757 14.5222C1.45122 14.6659 1.53191 14.8607 1.53191 15.0638V22.4681H22.4681V15.0638C22.4681 14.8607 22.5488 14.6659 22.6924 14.5222C22.8361 14.3786 23.0309 14.2979 23.234 14.2979C23.4372 14.2979 23.632 14.3786 23.7757 14.5222C23.9193 14.6659 24 14.8607 24 15.0638ZM11.4587 15.6051C11.6023 15.7485 11.797 15.8291 12 15.8291C12.203 15.8291 12.3977 15.7485 12.5413 15.6051L17.6477 10.4987C17.783 10.3535 17.8566 10.1615 17.8531 9.96304C17.8496 9.76461 17.7692 9.57528 17.6289 9.43494C17.4886 9.2946 17.2992 9.21422 17.1008 9.21071C16.9024 9.20721 16.7103 9.28087 16.5651 9.41617L12.766 13.214V0.765957C12.766 0.562813 12.6853 0.367989 12.5416 0.224344C12.398 0.0806988 12.2031 0 12 0C11.7969 0 11.602 0.0806988 11.4584 0.224344C11.3147 0.367989 11.234 0.562813 11.234 0.765957V13.214L7.43489 9.41617C7.28969 9.28087 7.09765 9.20721 6.89921 9.21071C6.70078 9.21422 6.51145 9.2946 6.37111 9.43494C6.23077 9.57528 6.15039 9.76461 6.14688 9.96304C6.14338 10.1615 6.21704 10.3535 6.35234 10.4987L11.4587 15.6051Z"
                className={`fill-current`}
              />
            </svg>
            {slice.primary.cta.text}
          </PrismicLink>
        </div>
      )}
    </section>
  );
};

export default CarouselRecipes;
